import { type Words, WordsContext } from "@rally/ui-kit";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Preferences } from "../net/api.ts";
import { act, read } from "../net/rally.ts";
import {
  browserLanguage,
  isLanguageChoice,
  type Language,
  type LanguageChoice,
  resolveLanguage,
} from "./locale.ts";
import { say, setLanguage } from "./say.ts";

function theKitsWords(language: Language): Words {
  return {
    close: say("kit.close"),
    dismiss: say("kit.dismiss"),
    moreActions: say("kit.moreActions"),
    send: say("kit.send"),
    attach: say("kit.attach"),
    clearSearch: say("kit.clearSearch"),
    oneTimeCode: say("kit.oneTimeCode"),
    write: say("kit.write"),
    messages: say("kit.messages"),
    previousPage: say("kit.previousPage"),
    nextPage: say("kit.nextPage"),
    zoomIn: say("kit.zoomIn"),
    zoomOut: say("kit.zoomOut"),
    record: say("kit.record"),
    raiseHand: say("kit.raiseHand"),
    endCall: say("kit.endCall"),
    present: say("kit.present"),
    justNow: say("when.justNow"),
    minutesAgo: say("when.minutes"),
    hoursAgo: say("when.hours"),
    daysAgo: say("when.days"),
    locale: language,
  };
}

type LocaleState = {
  language: Language;
  preference: LanguageChoice;
  choose: (choice: LanguageChoice) => Promise<void>;
};

const LocaleContext = createContext<LocaleState | null>(null);

export function LocaleProvider({ account, children }: { account?: string; children: ReactNode }) {
  const [preference, setPreference] = useState<LanguageChoice>("auto");
  const [language, setResolved] = useState<Language>(() => browserLanguage());
  const revision = useRef(0);
  const pendingChoices = useRef(0);

  const apply = useCallback((choice: LanguageChoice) => {
    setPreference(choice);
    const next = resolveLanguage(choice);
    setLanguage(next);
    if (globalThis.document) document.documentElement.lang = next;
    setResolved(next);
  }, []);

  useEffect(() => {
    if (!account) {
      apply("auto");
      return;
    }
    let live = true;
    revision.current += 1;
    const refresh = () => {
      if (pendingChoices.current > 0) return Promise.resolve();
      const asked = ++revision.current;
      return read<Preferences>("my.preferences")
        .then((mine) => {
          if (live && asked === revision.current)
            apply(isLanguageChoice(mine.language) ? mine.language : "auto");
        })
        .catch(() => undefined);
    };
    void refresh();
    const every = setInterval(() => void refresh(), 30_000);
    const visible = () => document.visibilityState === "visible" && void refresh();
    document.addEventListener("visibilitychange", visible);
    return () => {
      live = false;
      revision.current += 1;
      clearInterval(every);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [account, apply]);

  const value = useMemo<LocaleState>(
    () => ({
      language,
      preference,
      choose: async (choice) => {
        const before = preference;
        const chosen = ++revision.current;
        pendingChoices.current += 1;
        apply(choice);
        try {
          if (account) await act("preferences.set", { setting: "language", value: choice });
        } catch (cause) {
          if (chosen === revision.current) apply(before);
          throw cause;
        } finally {
          pendingChoices.current -= 1;
        }
      },
    }),
    [account, apply, language, preference],
  );
  const words = useMemo(() => theKitsWords(language), [language]);
  return (
    <LocaleContext.Provider value={value}>
      <WordsContext.Provider value={words}>{children}</WordsContext.Provider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleState {
  const value = useContext(LocaleContext);
  if (!value) throw new Error("useLocale must be inside LocaleProvider");
  return value;
}
